import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { useCallback, useRef, useState } from 'react';
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';

import 'react-image-crop/dist/ReactCrop.css';

import { EditModeShell } from './EditModeShell';

import { useDialogContentContainer } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { logger } from '@/utils/logger';

export interface MediaStudioCropOverlayProps {
  imageUrl: string;
  /** 1 | 4/5 | 9/16 | 1.91 — undefined = freeform */
  initialAspectRatio?: number;
  onCancel: () => void;
  /** Returns the cropped image as a Blob. Parent handles upload. */
  onSave: (blob: Blob) => Promise<void> | void;
  isSaving?: boolean;
}

/**
 * Aspect labels lead with the use, not the math. Mora's ICP says "Reel" not
 * "9:16". The dropdown shows name + subtitle (use case) so non-designers can
 * scan by intent.
 */
const ASPECT_OPTIONS: ReadonlyArray<{
  id: string;
  label: string;
  subtitle: string;
  value: number | undefined;
  /** Compact ratio shown next to the label in the trigger button. */
  ratioLabel: string;
}> = [
  {
    id: 'free',
    label: 'Freeform',
    subtitle: 'Match the source',
    value: undefined,
    ratioLabel: '',
  },
  { id: '1-1', label: 'Square', subtitle: 'Feed posts · 1:1', value: 1, ratioLabel: '1:1' },
  {
    id: '4-5',
    label: 'Portrait',
    subtitle: 'Instagram portrait · 4:5',
    value: 4 / 5,
    ratioLabel: '4:5',
  },
  {
    id: '9-16',
    label: 'Reels & Stories',
    subtitle: 'TikTok · IG Reels · 9:16',
    value: 9 / 16,
    ratioLabel: '9:16',
  },
  {
    id: '1-91-1',
    label: 'Landscape',
    subtitle: 'Link previews · 1.91:1',
    value: 1.91,
    ratioLabel: '1.91:1',
  },
];

function findOptionId(value: number | undefined): string {
  if (value === undefined) return 'free';
  const match = ASPECT_OPTIONS.find(
    (opt) => opt.value !== undefined && Math.abs(opt.value - value) < 0.001,
  );
  return match?.id ?? 'free';
}

function RatioGlyph({
  value,
  selected = false,
}: {
  value: number | undefined;
  selected?: boolean;
}): React.ReactNode {
  const ratio = value ?? 1;
  const max = 18;
  const width = value === undefined ? 16 : ratio >= 1 ? max : Math.max(8, max * ratio);
  const height = value === undefined ? 16 : ratio >= 1 ? Math.max(8, max / ratio) : max;

  return (
    <span
      className={cn(
        'flex h-6 w-7 shrink-0 items-center justify-center text-foreground/70',
        selected && 'text-foreground',
      )}
      aria-hidden
    >
      <span
        className={cn(
          'rounded-[4px] border border-current',
          value === undefined && 'border-dashed',
          selected && 'bg-foreground/[0.04]',
        )}
        style={{ width, height }}
      />
    </span>
  );
}

function makeInitialCrop(
  aspect: number | undefined,
  mediaWidth: number,
  mediaHeight: number,
): Crop {
  if (aspect === undefined) {
    return centerCrop(
      makeAspectCrop({ unit: '%', width: 90 }, mediaWidth / mediaHeight, mediaWidth, mediaHeight),
      mediaWidth,
      mediaHeight,
    );
  }
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight,
  );
}

// Cap the rendered crop's longest edge. 8MP source → 24MB+ raw canvas →
// multi-second toBlob + 3-5MB upload. Capping at 2048px keeps social-media
// quality (Instagram caps at 1080-1440 anyway) and slashes both encode time
// and upload size by 4-10x. Production crop tools (Cloudinary, Photoshop
// Express on web) ship the same downscale-on-export pattern.
const MAX_OUTPUT_LONGEST_EDGE = 2048;
const WEBP_QUALITY = 0.85;

async function renderCroppedBlob(
  image: HTMLImageElement,
  pixelCrop: PixelCrop,
): Promise<Blob | null> {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const sourceW = Math.round(pixelCrop.width * scaleX);
  const sourceH = Math.round(pixelCrop.height * scaleY);
  // Downscale if the cropped area exceeds the cap on its longest edge.
  const longest = Math.max(sourceW, sourceH);
  const downscale = longest > MAX_OUTPUT_LONGEST_EDGE ? MAX_OUTPUT_LONGEST_EDGE / longest : 1;
  const targetW = Math.max(1, Math.round(sourceW * downscale));
  const targetH = Math.max(1, Math.round(sourceH * downscale));

  // Prefer OffscreenCanvas — keeps the encode off the main thread on
  // browsers that support it (Chrome, Edge, Firefox 105+, Safari 16.4+).
  // Falls back to a regular <canvas> for older browsers.
  const useOffscreen = typeof OffscreenCanvas !== 'undefined';
  const canvas: OffscreenCanvas | HTMLCanvasElement = useOffscreen
    ? new OffscreenCanvas(targetW, targetH)
    : Object.assign(document.createElement('canvas'), { width: targetW, height: targetH });
  if (!useOffscreen) {
    (canvas as HTMLCanvasElement).width = targetW;
    (canvas as HTMLCanvasElement).height = targetH;
  }
  const ctx = canvas.getContext('2d') as
    OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
  if (!ctx) {
    logger.error('MediaStudioCropOverlay: failed to get 2d context');
    return null;
  }
  // Highest-quality downscale resampling for sharper crops.
  if ('imageSmoothingQuality' in ctx) {
    (ctx as CanvasRenderingContext2D).imageSmoothingQuality = 'high';
  }
  ctx.drawImage(
    image,
    pixelCrop.x * scaleX,
    pixelCrop.y * scaleY,
    pixelCrop.width * scaleX,
    pixelCrop.height * scaleY,
    0,
    0,
    targetW,
    targetH,
  );
  if (useOffscreen) {
    return (canvas as OffscreenCanvas).convertToBlob({ type: 'image/webp', quality: WEBP_QUALITY });
  }
  return new Promise<Blob | null>((resolve) => {
    (canvas as HTMLCanvasElement).toBlob((blob) => resolve(blob), 'image/webp', WEBP_QUALITY);
  });
}

export function MediaStudioCropOverlay(props: MediaStudioCropOverlayProps): React.ReactNode {
  const { imageUrl, initialAspectRatio, onCancel, onSave, isSaving = false } = props;
  const container = useDialogContentContainer();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [aspect, setAspect] = useState<number | undefined>(initialAspectRatio);
  const [crop, setCrop] = useState<Crop | undefined>(undefined);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | undefined>(undefined);

  /**
   * Convert a percent-unit Crop to a pixel-unit PixelCrop using the rendered
   * image dimensions. ReactCrop emits onComplete only after a drag — so when
   * the user hits Save without manipulating the box, completedCrop is undefined
   * and the save handler silently returns. Seeding the pixel crop on image
   * load (and aspect change) keeps Save working out of the box.
   */
  function pixelFromPercent(c: Crop, img: HTMLImageElement): PixelCrop {
    return {
      unit: 'px',
      x: (c.x / 100) * img.width,
      y: (c.y / 100) * img.height,
      width: (c.width / 100) * img.width,
      height: (c.height / 100) * img.height,
    };
  }

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      const next = makeInitialCrop(aspect, img.width, img.height);
      setCrop(next);
      setCompletedCrop(pixelFromPercent(next, img));
    },
    [aspect],
  );

  const handleAspectChange = useCallback((id: string) => {
    const found = ASPECT_OPTIONS.find((opt) => opt.id === id);
    const nextValue = found?.value;
    setAspect(nextValue);
    const img = imgRef.current;
    if (img) {
      const next = makeInitialCrop(nextValue, img.width, img.height);
      setCrop(next);
      setCompletedCrop(pixelFromPercent(next, img));
    }
  }, []);

  const handleSave = useCallback(async () => {
    const img = imgRef.current;
    if (!img) {
      logger.error('MediaStudioCropOverlay: image ref missing on save');
      return;
    }
    // Fallback: if user hit Save without ever dragging the crop box, derive the
    // pixel crop from the current `crop` state instead of bailing silently.
    const pxCrop =
      completedCrop && completedCrop.width && completedCrop.height
        ? completedCrop
        : crop
          ? pixelFromPercent(crop, img)
          : null;
    if (!pxCrop || !pxCrop.width || !pxCrop.height) {
      logger.error('MediaStudioCropOverlay: no crop selection to save');
      return;
    }
    try {
      const blob = await renderCroppedBlob(img, pxCrop);
      if (!blob) return;
      await onSave(blob);
    } catch (err) {
      logger.error('MediaStudioCropOverlay: save failed', err);
    }
  }, [completedCrop, crop, onSave]);

  // Save is enabled the moment the image has loaded and given us a crop —
  // either a user-dragged `completedCrop` or the seeded `crop` from
  // onImageLoad. The handleSave callback already has the crop→pixel
  // fallback; mirror it here so the button isn't disabled on a freshly
  // opened crop overlay where the user clicks Save without dragging.
  const hasUsableCrop =
    (completedCrop && completedCrop.width > 0 && completedCrop.height > 0) ||
    (crop && (crop.width ?? 0) > 0 && (crop.height ?? 0) > 0);
  const canSave = !!hasUsableCrop && !isSaving;
  const selectedOptionId = findOptionId(aspect);
  const selectedOption = ASPECT_OPTIONS.find((opt) => opt.id === selectedOptionId);

  return (
    <EditModeShell
      title="Cropping"
      onCancel={onCancel}
      onDone={handleSave}
      doneDisabled={!canSave}
      doneLoading={isSaving}
      doneTooltip={!canSave ? 'Drag to set a crop area' : undefined}
      toolSlot={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={isSaving}
              className="inline-flex h-8 items-center gap-1.5 rounded-full border border-border/60 bg-transparent px-2.5 text-caption font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-muted data-[state=open]:text-foreground"
              aria-label="Aspect ratio"
            >
              <RatioGlyph value={selectedOption?.value} />
              <span className="text-foreground/85">{selectedOption?.label ?? 'Freeform'}</span>
              {selectedOption?.ratioLabel ? (
                <span className="text-detail text-muted-foreground/70">
                  {selectedOption.ratioLabel}
                </span>
              ) : null}
              <CaretDownIcon className="size-3" weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-72 rounded-2xl p-2 shadow-elevation-3"
            aria-label="Aspect ratio"
            container={container}
          >
            <div className="px-2 pb-1 pt-1.5 text-detail font-medium leading-snug text-muted-foreground">
              Crop to aspect ratio
            </div>
            {ASPECT_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt.id}
                onSelect={() => handleAspectChange(opt.id)}
                className="flex items-center gap-3 rounded-xl px-2.5 py-2.5"
              >
                <RatioGlyph value={opt.value} selected={opt.id === selectedOptionId} />
                <div className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-1.5 text-sm font-medium text-foreground">
                    <span>{opt.label}</span>
                    {opt.ratioLabel ? (
                      <span className="text-muted-foreground/75">{opt.ratioLabel}</span>
                    ) : null}
                  </span>
                  <span className="block text-detail text-muted-foreground">{opt.subtitle}</span>
                </div>
                <span className="flex size-4 shrink-0 items-center justify-center">
                  {opt.id === selectedOptionId ? (
                    <CheckIcon className="size-3.5 text-foreground" weight="bold" />
                  ) : null}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      <ReactCrop
        crop={crop}
        onChange={(_, percentCrop) => setCrop(percentCrop)}
        onComplete={(c) => setCompletedCrop(c)}
        aspect={aspect}
        minWidth={32}
        minHeight={32}
        ruleOfThirds
        className="block max-h-[min(70vh,640px)] max-w-full"
      >
        <img
          ref={imgRef}
          src={imageUrl}
          alt="Crop region"
          crossOrigin="anonymous"
          onLoad={onImageLoad}
          className="max-h-[min(70vh,640px)] max-w-full object-contain"
        />
      </ReactCrop>
    </EditModeShell>
  );
}
